"use client"

import React, { type ReactNode } from "react"
import { cn } from "@/lib/utils"

/** Tool header inside the hub canvas: Manrope title, one-line description, actions on the right */
export function ToolHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn("mb-3 flex flex-wrap items-end justify-between gap-3", className)}>
      <div className="min-w-0">
        <h1 className="font-display text-xl font-extrabold tracking-tight text-foreground">{title}</h1>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/** White card on the canvas with the Hub's border, radius and soft shadow */
export function HubCard({ children, className, accent }: { children: ReactNode; className?: string; accent?: string }) {
  return (
    <div className={cn("relative overflow-hidden rounded-lg border border-card-border bg-card shadow-card", className)}>
      {accent && <span className="absolute inset-y-0 left-0 w-1" style={{ background: accent }} aria-hidden />}
      {children}
    </div>
  )
}

/** Pill button for tool toolbars (search, filters, actions) */
export function ToolbarPill({
  active = false,
  onClick,
  icon,
  children,
  disabled,
  title,
  variant = "outline",
  className,
}: {
  active?: boolean
  onClick?: () => void
  icon?: ReactNode
  children: ReactNode
  disabled?: boolean
  title?: string
  variant?: "outline" | "primary" | "danger"
  className?: string
}) {
  const styles =
    variant === "primary"
      ? "border-primary bg-primary text-primary-foreground hover:bg-[#081229] dark:hover:bg-primary/90"
      : variant === "danger"
        ? "border-destructive/40 bg-card text-destructive hover:bg-destructive/5"
        : active
          ? "border-[var(--th-active-pill-border)] bg-accent text-accent-foreground"
          : "border-control-border bg-card text-[var(--gw-text-body)] hover:border-[var(--th-control-border-hover)]"
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn("inline-flex h-9 items-center gap-1.5 rounded-[9px] border px-3 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50", styles, className)}
    >
      {icon}
      {children}
    </button>
  )
}

export interface SegmentOption<T extends string> {
  value: T
  label: ReactNode
  count?: number
  tone?: "default" | "danger"
}

/** The Hub's equal-width segmented track: grey rail, filled navy active segment */
export function SegmentedPills<T extends string>({ options, value, onChange, className }: { options: SegmentOption<T>[]; value: T; onChange: (value: T) => void; className?: string }) {
  return (
    <div role="group" className={cn("inline-flex gap-0.5 rounded-lg border border-card-border bg-muted p-0.5", className)}>
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
              active ? "bg-primary text-primary-foreground" : option.tone === "danger" ? "text-destructive hover:bg-card" : "text-muted-foreground hover:bg-card hover:text-foreground"
            )}
          >
            {option.label}
            {option.count !== undefined && <span className={cn("text-[11px] font-bold", active ? "text-primary-foreground/75" : "text-subtle")}>{option.count}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** Small status pill (Hub `StatusPill`) */
export function StatusPill({ children, tone = "neutral", className, title }: { children: ReactNode; tone?: "neutral" | "active" | "warning" | "danger" | "success"; className?: string; title?: string }) {
  const tones = {
    neutral: "border-card-border bg-muted text-muted-foreground",
    active: "border-[var(--th-active-pill-border)] bg-accent text-accent-foreground",
    warning: "border-[#ead9b0] bg-[#fbf1dd] text-[#8a5800]",
    danger: "border-[#d88a8a] bg-[#f8e0e0] text-[#790000]",
    success: "border-[#8abf9f] bg-[#e6f1ea] text-[#01692b]",
  }
  return (
    <span title={title} className={cn("inline-flex items-center whitespace-nowrap rounded-pill border px-2 py-0.5 text-[11px] font-semibold", tones[tone], className)}>
      {children}
    </span>
  )
}

/** Uppercase table header cell in the Hub style */
export const hubTh = "th-section-label h-9 whitespace-nowrap"
/** Row hover in the Hub style */
export const hubTr = "border-card-border hover:bg-row-hover"
